import { createFeature, createReducer, on } from "@ngrx/store";
import { InviteMutationState, PendingInvite } from "../models/invite.models";
import { InvitesActions } from "./invites.actions";

export interface InvitesState {
    invites: PendingInvite[];
    query: string;
    total: number;
    pageSize: number;
    pageIndex: number;
    pageTokens: string[];
    nextToken: string | null;
    loading: boolean;
    loaded: boolean;
    error: boolean;
    mutation: InviteMutationState | null;
}

const initialState: InvitesState = {
    invites: [],
    query: "",
    total: 0,
    pageSize: 30,
    pageIndex: 0,
    pageTokens: [""],
    nextToken: null,
    loading: false,
    loaded: false,
    error: false,
    mutation: null,
};

export const invitesFeature = createFeature({
    name: "invites",
    reducer: createReducer(
        initialState,
        on(InvitesActions.pageOpened, (state) => ({
            ...state,
            loading: true,
            error: false,
        })),
        on(InvitesActions.pageRequested, (state) => ({
            ...state,
            loading: true,
            error: false,
        })),
        on(
            InvitesActions.pageLoaded,
            (state, { invites, total, nextToken, token, pageIndex }) => {
                const pageTokens = [...state.pageTokens];
                pageTokens[pageIndex] = token;
                return {
                    ...state,
                    invites,
                    total,
                    nextToken,
                    pageIndex,
                    pageTokens,
                    loading: false,
                    loaded: true,
                    error: false,
                };
            }
        ),
        on(InvitesActions.pageLoadFailed, (state) => ({
            ...state,
            loading: false,
            loaded: true,
            error: true,
        })),
        on(InvitesActions.queryChanged, (state, { query }) => ({
            ...state,
            query,
        })),
        on(InvitesActions.resendRequested, (state, { invite }) => ({
            ...state,
            mutation: {
                inviteId: invite.id,
                email: invite.email,
                type: "resend" as const,
                status: "pending" as const,
            },
        })),
        on(InvitesActions.resendSucceeded, (state, { invite }) => ({
            ...state,
            mutation: {
                inviteId: invite.id,
                email: invite.email,
                type: "resend" as const,
                status: "success" as const,
            },
        })),
        on(InvitesActions.resendFailed, (state, { invite }) => ({
            ...state,
            mutation: {
                inviteId: invite.id,
                email: invite.email,
                type: "resend" as const,
                status: "error" as const,
            },
        })),
        on(InvitesActions.revokeRequested, (state, { invite }) => ({
            ...state,
            mutation: {
                inviteId: invite.id,
                email: invite.email,
                type: "revoke" as const,
                status: "pending" as const,
            },
        })),
        on(InvitesActions.revokeSucceeded, (state, { invite }) => ({
            ...state,
            invites: state.invites.filter((item) => item.id !== invite.id),
            total: Math.max(0, state.total - 1),
            mutation: {
                inviteId: invite.id,
                email: invite.email,
                type: "revoke" as const,
                status: "success" as const,
            },
        })),
        on(InvitesActions.revokeFailed, (state, { invite }) => ({
            ...state,
            mutation: {
                inviteId: invite.id,
                email: invite.email,
                type: "revoke" as const,
                status: "error" as const,
            },
        })),
        on(InvitesActions.mutationStatusCleared, (state) => ({
            ...state,
            mutation: null,
        }))
    ),
});
